import { useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  Loader2,
  RefreshCw,
  Send,
  Upload,
  UserPlus,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import {
  formatInviteDate,
  INVITE_ROLE_OPTIONS,
  inviteRoleLabel,
  invitationStatusLabel,
  invitationStatusVariant,
  validateEmail,
} from './companyOnboarding';
import {
  inviteTemplateUrl,
  singleInviteRow,
  useBulkInviteConfirm,
  useBulkInvitePreview,
  useCompanyInvitations,
  useResendInvitation,
  type BulkPreviewResult,
  type BulkPreviewRow,
} from './hooks/usePlatformAdminQueries';

const DEFAULT_ROLE = 'agronom';

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.detail || fallback : fallback;
}

/** Сводка «сколько строк файла годится» — одной строкой, без таблицы. */
function previewSummary(preview: BulkPreviewResult): string {
  const parts = [`годятся: ${preview.valid_rows.length}`];
  if (preview.duplicate_rows.length) {
    parts.push(`уже приглашены: ${preview.duplicate_rows.length}`);
  }
  if (preview.invalid_rows.length) {
    parts.push(`с ошибками: ${preview.invalid_rows.length}`);
  }
  return parts.join(' · ');
}

function ProblemRows({
  title,
  rows,
}: {
  title: string;
  rows: BulkPreviewRow[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-foreground">{title}</p>
      <ul className="space-y-1">
        {rows.slice(0, 20).map((r) => (
          <li key={r.row} className="text-xs text-muted-foreground">
            строка {r.row}: {r.email ?? 'без почты'} — {r.errors.join('; ')}
          </li>
        ))}
        {rows.length > 20 && (
          <li className="text-xs text-muted-foreground">
            …и ещё {rows.length - 20}
          </li>
        )}
      </ul>
    </div>
  );
}

interface CompanyInvitationsPanelProps {
  tenantId: string;
  tenantName: string;
}

/**
 * Приглашения компании из платформенной админки (AK-572).
 *
 * Три вещи в одном месте, потому что они — один сценарий «позвать людей»:
 * позвать одного, залить список файлом, посмотреть кто уже вошёл.
 *
 * Одиночное приглашение уходит в ту же ручку `invitations/bulk` одной
 * строкой — отдельной ручки на бэкенде нет, а лепить xlsx на клиенте ради
 * одного человека значит завести второй парсер формата, который однажды
 * разойдётся с серверным.
 *
 * Кнопки «скопировать ссылку» в списке нет намеренно: приглашения, созданные
 * админкой, хранят токен хешем — собрать ссылку не из чего. (Исключение —
 * приглашение первого администратора из `create_tenant.py`: там в колонке
 * лежит сырой токен. Это дефект хранения, а не повод отдавать ссылку в API:
 * кнопка работала бы через раз и закрепляла бы дефект.) Не дошло письмо —
 * приглашаем заново.
 */
export function CompanyInvitationsPanel({
  tenantId,
  tenantName,
}: CompanyInvitationsPanelProps) {
  const invitations = useCompanyInvitations(tenantId);
  const preview = useBulkInvitePreview();
  const confirm = useBulkInviteConfirm();
  const resend = useResendInvitation();
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Ошибка переотправки живёт рядом со списком, а не в `bulkError`: тот
  // рисуется в блоке «Пригласить списком» над загрузкой файла, и человек,
  // нажавший «Отправить ещё раз» у строки внизу, красное сообщение вверху
  // просто не связывал бы со своим действием.
  const [resendError, setResendError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState(DEFAULT_ROLE);
  const [singleError, setSingleError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [previewData, setPreviewData] = useState<BulkPreviewResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const rows = invitations.data ?? [];

  const sendSingle = async () => {
    setNotice(null);
    const emailError = validateEmail(email);
    setSingleError(emailError);
    if (emailError) return;
    try {
      const result = await confirm.mutateAsync({
        tenantId,
        rows: [singleInviteRow({ email, firstName, lastName, role })],
      });
      if (result.created === 0) {
        setSingleError(
          result.errors[0] ??
            'Приглашение не создано — возможно, этот человек уже приглашён',
        );
        return;
      }
      setEmail('');
      setFirstName('');
      setLastName('');
      setNotice(`Приглашение отправлено. Письмо ушло на ${result.created === 1 ? 'указанную почту' : 'почты'}.`);
    } catch (err) {
      setSingleError(errText(err, 'Не удалось отправить приглашение'));
    }
  };

  const onFile = async (file: File | null) => {
    setBulkError(null);
    setNotice(null);
    setPreviewData(null);
    if (!file) return;
    setFileName(file.name);
    try {
      setPreviewData(await preview.mutateAsync({ tenantId, file }));
    } catch (err) {
      setBulkError(errText(err, 'Не удалось прочитать файл'));
    }
  };

  const confirmBulk = async () => {
    if (!previewData || previewData.valid_rows.length === 0) return;
    setBulkError(null);
    try {
      const result = await confirm.mutateAsync({
        tenantId,
        rows: previewData.valid_rows,
      });
      setPreviewData(null);
      setFileName(null);
      if (fileRef.current) fileRef.current.value = '';
      setNotice(
        result.errors.length
          ? `Приглашено: ${result.created}. Часть строк не прошла — ${result.errors.join('; ')}`
          : `Приглашено: ${result.created}. Письма отправлены.`,
      );
    } catch (err) {
      setBulkError(errText(err, 'Не удалось отправить приглашения'));
    }
  };

  const resendInvite = async (invitationId: string) => {
    setNotice(null);
    setResendError(null);
    setResendingId(invitationId);
    try {
      const fresh = await resend.mutateAsync({ tenantId, invitationId });
      setNotice(
        fresh.email_sent === false
          ? `Приглашение для ${fresh.email} обновлено, но письмо не ушло — почта отказала. Срок продлён; попробуйте отправить ещё раз позже.`
          : `Отправили приглашение ещё раз на ${fresh.email}.`,
      );
    } catch (err) {
      setResendError(errText(err, 'Не удалось отправить приглашение ещё раз'));
    } finally {
      setResendingId(null);
    }
  };

  const busy = confirm.isPending;

  return (
    <div className="space-y-4 px-2 py-2">
      {/* Одиночное приглашение */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Пригласить в «{tenantName}»
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[220px] flex-1 space-y-1">
            <Label htmlFor={`invite-email-${tenantId}`}>Почта</Label>
            <Input
              id={`invite-email-${tenantId}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agronom@company.ru"
              aria-invalid={!!singleError}
            />
          </div>
          <div className="min-w-[120px] flex-1 space-y-1">
            <Label htmlFor={`invite-first-${tenantId}`}>Имя</Label>
            <Input
              id={`invite-first-${tenantId}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Иван"
            />
          </div>
          <div className="min-w-[120px] flex-1 space-y-1">
            <Label htmlFor={`invite-last-${tenantId}`}>Фамилия</Label>
            <Input
              id={`invite-last-${tenantId}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Иванов"
            />
          </div>
          <div className="min-w-[180px] space-y-1">
            <Label htmlFor={`invite-role-${tenantId}`}>Роль</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id={`invite-role-${tenantId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INVITE_ROLE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" onClick={() => void sendSingle()} disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Пригласить
          </Button>
        </div>
        {singleError && (
          <p role="alert" className="text-xs text-destructive">
            {singleError}
          </p>
        )}
      </div>

      {/* Массовая загрузка */}
      <div className="space-y-2 border-t border-border/70 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Пригласить списком
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={inviteTemplateUrl(tenantId)} download>
              <Download className="h-4 w-4" />
              Скачать шаблон
            </a>
          </Button>
          <Label
            htmlFor={`invite-file-${tenantId}`}
            className="text-sm font-normal text-muted-foreground"
          >
            Заполненный файл
          </Label>
          <Input
            id={`invite-file-${tenantId}`}
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="max-w-[280px]"
            onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
          />
          {preview.isPending && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Читаем файл…
            </span>
          )}
        </div>

        {bulkError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{bulkError}</AlertDescription>
          </Alert>
        )}

        {previewData && (
          <div className="space-y-2 rounded-md border border-border bg-background p-3">
            <p className="text-sm text-foreground">
              {fileName}: {previewSummary(previewData)}
            </p>
            <ProblemRows
              title="Уже приглашены или дубли в файле"
              rows={previewData.duplicate_rows}
            />
            <ProblemRows title="Строки с ошибками" rows={previewData.invalid_rows} />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy || previewData.valid_rows.length === 0}
                onClick={() => void confirmBulk()}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Пригласить {previewData.valid_rows.length}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setPreviewData(null);
                  setFileName(null);
                  if (fileRef.current) fileRef.current.value = '';
                }}
              >
                Отменить
              </Button>
            </div>
          </div>
        )}

        {!previewData && !preview.isPending && !bulkError && (
          <p className="text-xs text-muted-foreground">
            <Upload className="mr-1 inline h-3 w-3" />
            Колонки шаблона: почта, имя, фамилия, роль, сообщение. Перед
            отправкой покажем, сколько строк годится.
          </p>
        )}
      </div>

      {notice && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {/* Список отправленных */}
      <div className="space-y-2 border-t border-border/70 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Отправленные приглашения
          {invitations.isSuccess ? ` — ${rows.length}` : ''}
        </p>

        {resendError && (
          <p role="alert" className="text-sm text-destructive">
            {resendError}
          </p>
        )}

        {invitations.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}

        {invitations.isError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">Не удалось загрузить приглашения</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void invitations.refetch()}
            >
              Повторить
            </Button>
          </div>
        )}

        {invitations.isSuccess && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Пока никого не приглашали. Начните с администратора компании — он
            позовёт остальных сам.
          </p>
        )}

        <div className="divide-y divide-border/60">
          {rows.map((inv) => (
            <div
              key={inv.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
            >
              <div className="min-w-0 flex-1 basis-[220px]">
                <p className="truncate text-sm text-foreground">{inv.email}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {[inv.intended_last_name, inv.intended_first_name]
                    .filter(Boolean)
                    .join(' ') || 'без имени'}
                  {' · '}
                  {inviteRoleLabel(inv.intended_role)}
                </p>
              </div>
              <div className="basis-[150px] text-xs text-muted-foreground">
                позвали {formatInviteDate(inv.created_at)}
              </div>
              <div className="basis-[150px] text-xs text-muted-foreground">
                {inv.status === 'accepted'
                  ? `вошёл ${formatInviteDate(inv.accepted_at)}`
                  : `годна до ${formatInviteDate(inv.expires_at)}`}
              </div>
              <Badge variant={invitationStatusVariant(inv.status)}>
                {invitationStatusLabel(inv.status)}
              </Badge>

              {inv.status === 'accepted' ? null : (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={resendingId === inv.id}
                  onClick={() => void resendInvite(inv.id)}
                >
                  {resendingId === inv.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Отправить ещё раз
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CompanyInvitationsPanel;
